import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye } from 'lucide-react';
import StuiRichText from './StuiRichText';
import StuiDateTime from './StuiDateTime';
import { formatDateTime, partsInZone, partsToValue } from '../../utils/dateTime';
import { emailApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import StuiSelect from './StuiSelect';

/**
 * Portable email popup. The caller decides who it goes to; the user writes the subject and a
 * rich-text body. The subject PREFIX belongs to the server ("DAWN Help (GWA): ",
 * "DAWN Notice (GF): ") and shows here as a locked box to the left of the subject.
 *
 *   <StuiEmailModal
 *     audience="support"                 // resolved to addresses by POST /api/email/send
 *     audienceLabel="DAWN Support"       // what the To line shows
 *     subjectQualifier="GWA"             // optional database name for the prefix
 *     subject="Panels"                   // initial subject after the prefix; the user can change it
 *     context={{ Page: 'Panels', Database: 'GWA' }}  // appended to the email as small print
 *     onClose={() => setShowEmail(false)}
 *     onSent={() => ...}                 // optional; onClose is also called after a send
 *   />
 *
 * Broadcast additions (Admin > Users), all optional, so a caller that passes none of them gets
 * the plain popup:
 *   recipientIds     AppSec_Users ids for audience "users"; the server looks up the addresses.
 *   recipients       [{ name, email }] for the read-only recipient list behind the To line's eye.
 *   query            the search that produced recipientIds, recorded in the audit row.
 *   templates        canned messages (config/emailTemplates.js), one always selected
 *                    (defaultTemplate, else the first): a Template picker that seeds
 *                    subject and body. A template's fields (date-time pickers, selects) replace
 *                    their [Slot] text live as they are picked. Send waits until every field is
 *                    picked and every [Slot] the template brought in is gone.
 *   subjectTools     ({ template, setSubject, setBodyHtml }) => node, rendered at the end of the
 *                    subject row, e.g. Admin > Users' rev log picker for the Release template.
 *   allowPreview     adds Preview: the server renders the real email (POST /api/email/preview).
 *   reviewThreshold  above this many recipients, Send becomes Review & Send, so a big send is
 *                    only possible from the preview.
 *
 * Audience, not addresses: the server resolves recipients, so no caller can aim the no-reply
 * mailbox at an arbitrary inbox. Rendered through a portal so it stacks above whatever popup
 * opened it.
 */

const plainText = (html) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
const hasText = (html) => plainText(html).trim().length > 0;

const escapeHtml = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Mirrors EmailController.SubjectPrefix, which is what is actually sent. */
const subjectPrefix = (audience, qualifier) => {
  const tag = audience === 'support' ? 'DAWN Help' : 'DAWN Notice';
  const q = (qualifier || '').replace(/[():\r\n]/g, '').trim().slice(0, 60);
  return q ? `${tag} (${q}): ` : `${tag}: `;
};

/**
 * A datetime field's starting value: now, rounded down to its minute step, plus its
 * defaultOffsetMinutes (Maintenance End starts an hour after Start).
 */
const nowFieldValue = (field) => {
  // The person's wall clock (Timezone preference, else the browser's), which is what the
  // 'local' value and its zone label are read in.
  const step = (field.minuteStep || 1) * 60000;
  const t = Math.floor(Date.now() / step) * step + (field.defaultOffsetMinutes || 0) * 60000;
  const p = partsInZone(new Date(t));
  return partsToValue(p, { mode: 'datetime', valueZone: 'local' });
};

/** What a field puts in the subject and in the body. A select can word the two differently. */
const fieldTexts = (field, raw) => {
  if (!raw) return null;
  if (field.type === 'select') {
    const opt = field.options.find(o => o.value === raw);
    return opt ? { subject: opt.label, body: opt.text || opt.label } : null;
  }
  // Written with the sender's zone: recipients are spread across zones, so a bare "10 PM" is
  // ambiguous. timeIfToday fields drop the date when it is today ("as of 10:15 PM EDT").
  const v = formatDateTime(raw, { mode: 'datetime', valueZone: 'local', style: 'long', showZone: true, timeIfToday: field.timeIfToday });
  if (!v) return null;
  // subjectDateOnly: the subject carries the date and the body the full time, so the email's
  // subject panel does not repeat the body.
  const subject = field.subjectDateOnly ? formatDateTime(raw, { mode: 'date', valueZone: 'local', style: 'long' }) : v;
  return { subject, body: v };
};

/** Swap a field's current text (its [Slot] until first picked) for its new text. */
const swapFieldText = (subject, bodyHtml, field, before, after) => {
  const from = before || { subject: field.slot, body: field.slot };
  const to = after || { subject: field.slot, body: field.slot };
  return {
    subject: subject.split(from.subject).join(to.subject),
    bodyHtml: bodyHtml.split(escapeHtml(from.body)).join(escapeHtml(to.body)),
  };
};

const slotsIn = (text) => text.match(/\[[^\]]+\]/g) || [];

/** Subject, body and field values a template starts with, defaults already swapped in. */
const templateState = (t, fallbackSubject) => {
  if (!t) return { key: '', subject: fallbackSubject, bodyHtml: '', values: {}, texts: {} };
  let subject = t.subject;
  let bodyHtml = t.bodyHtml;
  const values = {};
  const texts = {};
  (t.fields || []).forEach(f => {
    if (!f.defaultNow) return;
    values[f.key] = nowFieldValue(f);
    texts[f.key] = fieldTexts(f, values[f.key]);
    ({ subject, bodyHtml } = swapFieldText(subject, bodyHtml, f, null, texts[f.key]));
  });
  return { key: t.key, subject, bodyHtml, values, texts };
};

const StuiEmailModal = ({
  audience,
  audienceLabel,
  subjectQualifier,
  subject: initialSubject = '',
  context,
  title = 'Send Email',
  placeholder = 'Write your message...',
  onClose,
  onSent,
  recipientIds,
  recipients,
  query,
  templates,
  defaultTemplate,
  subjectTools,
  allowPreview = false,
  reviewThreshold,
}) => {
  const { notify } = useNotifications();
  // With templates there is always one selected: defaultTemplate, else the first.
  const [initial] = useState(() => templateState(
    templates?.length ? (templates.find(t => t.key === defaultTemplate) || templates[0]) : null,
    initialSubject
  ));
  const [subject, setSubject] = useState(initial.subject);
  const [bodyHtml, setBodyHtml] = useState(initial.bodyHtml);
  const [sending, setSending] = useState(false);
  const [templateKey, setTemplateKey] = useState(initial.key);
  const [fieldValues, setFieldValues] = useState(initial.values);
  const [showRecipients, setShowRecipients] = useState(false);
  const [review, setReview] = useState(null);      // { subject, html, recipientCount }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const fieldTextRef = useRef(initial.texts);       // key -> { subject, body } currently in the text

  const prefix = subjectPrefix(audience, subjectQualifier);
  const template = templates?.find(t => t.key === templateKey) || null;
  const fields = useMemo(() => template?.fields || [], [template]);

  const pickTemplate = (key) => {
    const next = templateState(templates?.find(x => x.key === key) || null, initialSubject);
    fieldTextRef.current = next.texts;
    setTemplateKey(next.key);
    setFieldValues(next.values);
    setSubject(next.subject);
    setBodyHtml(next.bodyHtml);
  };

  const setField = (field, raw) => {
    const texts = fieldTexts(field, raw);
    const swapped = swapFieldText(subject, bodyHtml, field, fieldTextRef.current[field.key], texts);
    fieldTextRef.current[field.key] = texts;
    setFieldValues(v => ({ ...v, [field.key]: raw }));
    setSubject(swapped.subject);
    setBodyHtml(swapped.bodyHtml);
  };

  // Slots are the template's own [bracket] text, so a bracket the sender types is never flagged.
  const templateSlots = useMemo(
    () => (template ? [...new Set([...slotsIn(template.subject), ...slotsIn(plainText(template.bodyHtml))])] : []),
    [template]
  );
  const unfilledSlots = templateSlots.filter(slot => subject.includes(slot) || plainText(bodyHtml).includes(slot));
  const missingFields = fields.filter(f => !fieldTexts(f, fieldValues[f.key]));

  const recipientCount = recipientIds ? recipientIds.length : null;
  const needsReview = allowPreview && reviewThreshold != null && recipientCount != null && recipientCount > reviewThreshold;

  const contextLabels = Object.keys(context || {}).filter(k => context[k]);
  const ready = subject.trim().length > 0 && hasText(bodyHtml)
    && missingFields.length === 0 && unfilledSlots.length === 0;

  const payload = () => ({
    audience,
    subjectQualifier,
    subject: subject.trim(),
    bodyHtml,
    context,
    recipientIds,
    template: template?.name,
    tone: template?.tone,
    query,
  });

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await emailApi.preview(payload());
      setReview(res.data);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to build the preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      await emailApi.send(payload());
      notify.success('Email sent');
      onSent?.();
      onClose?.();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to send email');
      setSending(false);
    }
  };

  const busy = sending || loadingPreview;
  const sendLabel = recipientCount != null && recipientCount > 1 ? `Send to ${recipientCount} users` : 'Send';

  const toLine = (
    <div className="stui-field-group stui-mb-md">
      <label>To</label>
      <div className="stui-row-8">
        <div className="stui-field-display stui-field-readonly">{audienceLabel || audience}</div>
        {recipients?.length > 0 && (
          <button className="stui-crud-btn" onClick={() => setShowRecipients(true)} title="View recipient addresses">
            <Eye size={16} />
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(
    <>
      <div className="stui-modal-overlay">
        <div className="stui-modal stui-modal-lg">
          <div className="stui-modal-header">
            <h3>{review ? 'Review Email' : title}</h3>
            <button className="stui-modal-close" onClick={onClose} disabled={sending}>
              <X size={20} />
            </button>
          </div>

          {review ? (
            <div className="stui-modal-body">
              {toLine}
              <div className="stui-field-group stui-mb-md">
                <label>Subject</label>
                <div className="stui-field-display stui-field-readonly">{review.subject}</div>
              </div>
              <div className="stui-demo-frame-lg">
                <iframe title="Email preview" srcDoc={review.html} sandbox="" width="100%" height="460" frameBorder="0" />
              </div>
              <p className="stui-label-note">
                This is the email as recipients get it. Outlook desktop can draw small details differently.
              </p>
            </div>
          ) : (
            <div className="stui-modal-body">
              {toLine}
              {templates?.length > 0 && (
                <div className="stui-field-group stui-mb-md">
                  <label>Template</label>
                  <StuiSelect className="stui-field-input" value={templateKey} onChange={e => pickTemplate(e.target.value)} disabled={busy}>
                    {templates.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                  </StuiSelect>
                </div>
              )}
              {fields.length > 0 && (
                <div className="stui-form-row-inline">
                  {fields.map(f => (
                    <div key={f.key} className="stui-form-field stui-field-group">
                      <label>{f.label}</label>
                      {f.type === 'select' ? (
                        <StuiSelect className="stui-field-input" value={fieldValues[f.key] || ''} onChange={e => setField(f, e.target.value)}>
                          <option value="">Select...</option>
                          {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </StuiSelect>
                      ) : (
                        <StuiDateTime
                          mode="datetime"
                          valueZone="local"
                          minuteStep={f.minuteStep || 1}
                          showZone
                          value={fieldValues[f.key] || ''}
                          onChange={v => setField(f, v)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="stui-field-group stui-mb-md">
                <label>Subject</label>
                <div className="stui-row-8">
                  <div className="stui-field-display stui-field-readonly stui-field-prefix" title="Added to every message of this kind">{prefix.trim()}</div>
                  <input
                    type="text"
                    className="stui-field-input"
                    value={subject}
                    maxLength={200 - prefix.length}
                    onChange={e => setSubject(e.target.value)}
                  />
                  {subjectTools?.({ template, setSubject, setBodyHtml })}
                </div>
              </div>
              <StuiRichText value={bodyHtml} onChange={setBodyHtml} placeholder={placeholder} minHeight={220} />
              {template && (missingFields.length > 0 || unfilledSlots.length > 0) && (
                <p className="stui-label-note">
                  Before sending: {[
                    ...missingFields.map(f => `pick ${f.label}`),
                    ...unfilledSlots.filter(slot => !fields.some(f => f.slot === slot)).map(slot => `replace ${slot}`),
                  ].join(', ')}.
                </p>
              )}
              {contextLabels.length > 0 && (
                <p className="stui-label-note">
                  {contextLabels.join(', ')} and your name and email are included with the message.
                </p>
              )}
            </div>
          )}

          <div className="stui-modal-footer">
            {review ? (<>
              <button className="stui-btn stui-btn-secondary" onClick={() => setReview(null)} disabled={sending}>Back to Edit</button>
              <button className="stui-btn stui-btn-primary" onClick={send} disabled={sending}>
                {sending ? 'Sending...' : sendLabel}
              </button>
            </>) : (<>
              <button className="stui-btn stui-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              {allowPreview && !needsReview && (
                <button className="stui-btn stui-btn-secondary" onClick={openPreview} disabled={busy || !ready}>
                  {loadingPreview ? 'Building...' : 'Preview'}
                </button>
              )}
              {needsReview ? (
                <button className="stui-btn stui-btn-primary" onClick={openPreview} disabled={busy || !ready} title={`More than ${reviewThreshold} recipients: check the preview before it goes`}>
                  {loadingPreview ? 'Building...' : 'Review & Send'}
                </button>
              ) : (
                <button className="stui-btn stui-btn-primary" onClick={send} disabled={busy || !ready}>
                  {sending ? 'Sending...' : 'Send'}
                </button>
              )}
            </>)}
          </div>
        </div>
      </div>

      {showRecipients && (
        <div className="stui-modal-overlay stui-modal-overlay-nested">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>Recipients ({recipients.length})</h3>
              <button className="stui-modal-close" onClick={() => setShowRecipients(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body stui-modal-body-grid">
              <div className="stui-data-grid-container">
                <div className="stui-data-grid-scroll-area">
                  <table className="stui-data-grid stui-data-grid-auto">
                    <thead><tr><th>Name</th><th>Email</th></tr></thead>
                    <tbody>
                      {recipients.map(r => (
                        <tr key={r.email}><td>{r.name}</td><td className="stui-text-nowrap">{r.email}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowRecipients(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default StuiEmailModal;
